import React, { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import GlassCard from "./GlassCard";

/**
 * Opsora — ClickableKPITile
 *
 * Every KPI in Opsora opens a drill-down. The arrow indicator on hover signals
 * to users that the tile is interactive; cursor:pointer reinforces it.
 *
 * Pass `onClick` to wire a drill-down handler. Visual contract matches the
 * existing KPITile (3px coloured strip on the left) so screens look unchanged.
 */
const STRIP = {
  green: "var(--status-green)",
  amber: "var(--status-amber)",
  red: "var(--status-red)",
  copper: "var(--accent)",
  slate: "var(--slate-500)",
  blue: "var(--status-blue)",
};

export default function ClickableKPITile({
  label,
  value,
  sub,
  suffix,
  color = "slate",
  onClick,
  testId,
}) {
  const [hovered, setHovered] = useState(false);
  const stripColor = STRIP[color] || STRIP.slate;

  return (
    <GlassCard
      variant="sm"
      className="kpi-clickable page-enter"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        padding: 20,
        cursor: onClick ? "pointer" : "default",
        transform: hovered && onClick ? "translateY(-2px)" : "translateY(0)",
        transition: "transform 0.2s var(--ease-out-expo), box-shadow 0.2s ease",
      }}
      data-testid={testId}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 12,
          bottom: 12,
          width: 3,
          borderRadius: 999,
          background: stripColor,
        }}
      />
      {onClick && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 10,
            right: 12,
            color: hovered ? "var(--accent)" : "var(--slate-300)",
            transition: "color 0.18s ease, transform 0.18s ease",
            transform: hovered ? "translate(2px, -2px)" : "translate(0, 0)",
            pointerEvents: "none",
          }}
        >
          <ArrowUpRight size={14} />
        </span>
      )}
      <div style={{ paddingLeft: 12 }}>
        <div
          className="font-body text-caps"
          style={{ marginBottom: 6, fontWeight: 600, color: "var(--slate-700)" }}
        >
          {label}
        </div>
        <div
          className="font-display"
          style={{
            fontSize: 32,
            color: "var(--slate-900)",
            lineHeight: 1.05,
            display: "flex",
            alignItems: "baseline",
            gap: 4,
          }}
        >
          {value}
          {suffix && (
            <span className="font-body text-sm" style={{ fontWeight: 400, color: "var(--slate-500)" }}>
              {suffix}
            </span>
          )}
        </div>
        {sub && (
          <div className="font-body text-xs" style={{ marginTop: 6, color: "var(--slate-700)" }}>
            {sub}
          </div>
        )}
      </div>
    </GlassCard>
  );
}
